/*
 * MOD-118 — Product gallery: warm the not-yet-shown slides after the page has loaded.
 *
 * PROBLEM
 * Every gallery slide after the first is rendered with loading="lazy" (product-media-gallery.liquid
 * only passes lazy_load:false for media_position 1). A lazy <img> is fetched only once the browser
 * decides it is "near the viewport", and:
 *   - DESKTOP (thumbnail layout, MOD-084): non-active slides are `display: none`
 *     (section-main-product.css `.product--thumbnail .product__media-item:not(.is-active)`).
 *     A display:none image has no box, so it NEVER intersects the viewport => it is not fetched at
 *     all until the thumbnail is clicked. The full-size poster (up to 1946w) then starts from zero.
 *   - MOBILE: the slides live in a horizontal scroller, off to the right, past the lazy-load
 *     distance threshold => the fetch only starts as the swipe brings them in.
 * Either way the user waits on every first visit to a slide, and it feels instant afterwards
 * because the image is then in the HTTP cache. Exactly the reported symptom.
 *
 * FIX
 * After `load` + an idle callback (so nothing competes with the LCP or the initial render), walk the
 * remaining slides in the order the user will meet them — the ones after the active slide first,
 * then wrapping round — and promote each to `loading="eager"` at `fetchpriority="low"`, one at a
 * time. Same technique MOD-087 already uses for the frame-preview base slide: flipping the attribute
 * on the real <img> means the browser picks exactly the srcset candidate it would have picked
 * anyway, so the later reveal is a guaranteed cache hit (a detached `new Image()` could in principle
 * resolve `sizes` to a different candidate and download twice).
 *
 * Sequential + low priority so the warm-up never saturates the connection: the next slide is the
 * one that matters, the rest trickle in behind it. Thumbnail hover/press jumps its slide to the
 * front of the queue for the case where the user clicks faster than the warm-up walks.
 * Skipped entirely on Save-Data / 2G (the hover promotion still applies there).
 */
(function () {
  'use strict';

  var STAGGER_MS = 120;

  var connection = navigator.connection || {};
  var frugal = connection.saveData === true || /(^|-)2g$/.test(connection.effectiveType || '');

  /* Promote one lazy slide image to an immediate (but low-priority) fetch. Resolves once it has
     loaded, errored, or was already warm — the caller uses that to pace the queue. */
  function warm(img, priority) {
    return new Promise(function (resolve) {
      if (!img || img.dataset.galleryWarmed === '1') return resolve();
      img.dataset.galleryWarmed = '1';
      if (img.complete && img.naturalWidth > 0) return resolve();

      var done = function () {
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
        resolve();
      };
      img.addEventListener('load', done);
      img.addEventListener('error', done);

      img.fetchPriority = priority || 'low';
      img.loading = 'eager';
    });
  }

  /* Pending slide images, starting just after the active slide and wrapping around, so the very
     next swipe/click target is warmed first. Thumbnails are excluded (they are visible and load
     normally); only the full-size slide images are scoped in. */
  function pendingImages(gallery) {
    var items = Array.prototype.slice.call(gallery.querySelectorAll('.product__media-item'));
    var activeIndex = 0;
    for (var i = 0; i < items.length; i++) {
      if (items[i].classList.contains('is-active')) {
        activeIndex = i;
        break;
      }
    }

    var ordered = items.slice(activeIndex + 1).concat(items.slice(0, activeIndex + 1));
    var images = [];
    ordered.forEach(function (item) {
      var img = item.querySelector('img[loading="lazy"]');
      if (img && img.dataset.galleryWarmed !== '1') images.push(img);
    });
    return images;
  }

  function warmAll(gallery) {
    if (gallery.galleryWarming) return;
    var queue = pendingImages(gallery);
    if (!queue.length) return;

    gallery.galleryWarming = true;
    (function next() {
      var img = queue.shift();
      if (!img) {
        gallery.galleryWarming = false;
        return;
      }
      warm(img).then(function () {
        setTimeout(next, STAGGER_MS);
      });
    })();
  }

  /* Hovering or pressing a thumbnail is the strongest possible signal that this slide is about to
     be shown — fetch it at high priority right away rather than waiting for its turn. */
  function bindThumbnails(gallery) {
    var thumbnails = gallery.querySelectorAll('[data-target]');
    if (!thumbnails.length) return;

    thumbnails.forEach(function (thumbnail) {
      var target = thumbnail.dataset.target;
      if (!target) return;

      var onIntent = function () {
        var slide = gallery.querySelector('[data-media-id="' + target + '"]');
        if (slide) warm(slide.querySelector('img[loading="lazy"]'), 'high');
      };

      thumbnail.addEventListener('pointerenter', onIntent, { passive: true });
      thumbnail.addEventListener('pointerdown', onIntent, { passive: true });
      thumbnail.addEventListener('focusin', onIntent);
    });
  }

  function boot() {
    var galleries = document.querySelectorAll('media-gallery');
    if (!galleries.length) return;

    galleries.forEach(function (gallery) {
      bindThumbnails(gallery);
      if (!frugal) warmAll(gallery);
    });
  }

  function schedule() {
    var idle = window.requestIdleCallback
      ? function (cb) {
          window.requestIdleCallback(cb, { timeout: 3000 });
        }
      : function (cb) {
          setTimeout(cb, 800);
        };
    idle(boot);
  }

  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });
})();
